import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { MAIN_SITE_URL } from "@/utils/constants";
import { generateArticleJsonLd, generateBreadcrumbJsonLd } from "@/utils/seo-schema";

const articlePath = "/artykul/kakobuy-jak-korzystac";
const articleImage = "https://readdy.ai/api/search-image?query=Person%20hands%20on%20laptop%20keyboard%20at%20warm%20cream%20desk%20with%20notebook%20and%20coffee%20cup%2C%20instructional%20beginner%20guide%20concept%2C%20soft%20natural%20morning%20light%20from%20window%2C%20minimalist%20learning%20and%20shopping%20photography%2C%20warm%20beige%20and%20sand%20tones%2C%20calm%20focused%20atmosphere%2C%20european%20lifestyle%20editorial%2C%20step%20by%20step%20tutorial%20feeling&width=1200&height=800&seq=kakobuy-article-jak&orientation=landscape";

const schemas = [
  generateArticleJsonLd({
    headline: "Jak korzystać z Kakobuy — Poradnik dla Początkujących",
    description: "Kompletny poradnik zakupowy dla początkujących użytkowników Kakobuy w Polsce. Od rejestracji, przez wyszukiwanie produktów, aż po bezpieczną wysyłkę — wszystko krok po kroku.",
    datePublished: "2026-07-01",
    dateModified: "2026-07-21",
    imageUrl: articleImage,
    articlePath,
  }),
  generateBreadcrumbJsonLd([
    { name: "Strona główna", path: "/" },
    { name: "Jak korzystać z Kakobuy", path: articlePath },
  ]),
];

export default function KakobuyJakKorzystacArticle() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-20">
        <article className="w-full px-4 md:px-8 py-10 md:py-16">
          <div className="max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-600 mb-8 flex-wrap">
              <Link to="/" className="hover:text-foreground-900 transition-colors cursor-pointer">Strona główna</Link>
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></span>
              <span className="text-foreground-900 font-medium">Jak korzystać z Kakobuy</span>
            </nav>

            <header className="mb-10">
              <div className="inline-flex items-center gap-2 bg-primary-100 px-4 py-1.5 rounded-full mb-4">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-book-open-line text-primary-500 text-sm"></i></span>
                <span className="text-xs font-semibold text-primary-700 whitespace-nowrap">Poradnik zakupowy</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                Jak korzystać z Kakobuy — Poradnik dla Początkujących
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                Kompletny poradnik zakupowy dla początkujących użytkowników Kakobuy w Polsce. Od rejestracji, przez wyszukiwanie produktów, aż po bezpieczną wysyłkę — wszystko krok po kroku.
              </p>
              <div className="flex items-center gap-3 text-xs text-foreground-600 flex-wrap">
                <span>Ostatnia aktualizacja: Lipiec 2026</span>
                <span className="text-foreground-400">•</span>
                <span>Czas czytania: 12 minut</span>
              </div>
            </header>

            <div className="w-full h-56 md:h-72 rounded-xl overflow-hidden mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=Person%20hands%20on%20laptop%20keyboard%20at%20warm%20cream%20desk%20with%20notebook%20and%20coffee%20cup%2C%20instructional%20beginner%20guide%20concept%2C%20soft%20natural%20morning%20light%20from%20window%2C%20minimalist%20learning%20and%20shopping%20photography%2C%20warm%20beige%20and%20sand%20tones%2C%20calm%20focused%20atmosphere%2C%20european%20lifestyle%20editorial%2C%20step%20by%20step%20tutorial%20feeling&width=1200&height=800&seq=kakobuy-article-jak&orientation=landscape"
                alt="Jak korzystać z Kakobuy — Poradnik dla początkujących"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="prose-custom text-sm md:text-base text-foreground-800 leading-relaxed space-y-8">
              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Wprowadzenie — czym jest Kakobuy?</h2>
                <p className="mb-3">Kakobuy to platforma zakupowa, która działa jako pośrednik między międzynarodowymi kupującymi a sprzedawcami z Chin. Umożliwia ona zakup produktów — od odzieży i obuwia, przez elektronikę, aż po akcesoria — w cenach znacznie niższych niż w tradycyjnych sklepach.</p>
                <p className="mb-3">Dla polskich użytkowników Kakobuy stał się popularnym wyborem ze względu na ogromny wybór produktów, konkurencyjne ceny i wygodę korzystania z gotowych arkuszy (spreadsheetów), które znacznie ułatwiają znalezienie interesujących ofert.</p>
                <p>W tym poradniku pokażemy Ci krok po kroku, jak rozpocząć przygodę z Kakobuy i bezpiecznie dokonywać zakupów.</p>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Krok 1: Rejestracja na platformie</h2>
                <p className="mb-3">Pierwszym krokiem jest utworzenie konta na oficjalnej stronie Kakobuy. Proces rejestracji jest prosty i wymaga jedynie podstawowych danych — adresu e-mail i hasła. Platforma obsługuje język angielski, co warto mieć na uwadze.</p>
                <p className="mb-3">Po rejestracji otrzymasz dostęp do panelu użytkownika, gdzie możesz zarządzać zamówieniami, śledzić przesyłki i komunikować się ze sprzedawcami.</p>
                <div className="bg-accent-100/50 border border-accent-200/60 rounded-lg p-4">
                  <p className="text-sm text-foreground-700">Pamiętaj — zawsze loguj się tylko przez oficjalną stronę Kakobuy. Unikaj klikania w podejrzane linki i nie podawaj swoich danych logowania na nieznanych stronach.</p>
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Krok 2: Korzystanie z Kakobuy Spreadsheet</h2>
                <p className="mb-3">Kakobuy Spreadsheet to najszybszy sposób na znalezienie interesujących produktów. Zamiast ręcznie przeszukiwać platformę, otwierasz gotowy arkusz z linkami posegregowanymi według kategorii.</p>
                <ol className="list-decimal pl-5 space-y-2 text-sm">
                  <li>Otwórz Kakobuy Spreadsheet</li>
                  <li>Wybierz interesującą Cię kategorię (np. Shoes, Hoodies, T-Shirts)</li>
                  <li>Przeglądaj listę produktów — każdy zawiera link do oferty</li>
                  <li>Kliknij w link, aby przejść bezpośrednio do strony produktu</li>
                  <li>Sprawdź dostępne opcje (rozmiar, kolor) i dodaj do koszyka</li>
                </ol>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Krok 3: Składanie zamówienia i wybór wysyłki</h2>
                <p className="mb-3">Po dodaniu produktów do koszyka przechodzisz do finalizacji zamówienia. Na tym etapie ważne jest, aby:</p>
                <div className="space-y-2">
                  {[
                    "Sprawdzić dostępność wszystkich produktów w wybranych rozmiarach i kolorach",
                    "Poprosić o zdjęcia QC (kontroli jakości) — zobaczysz rzeczywisty wygląd produktów przed wysyłką",
                    "Wybrać odpowiednią linię wysyłkową — szybsza dostawa = wyższy koszt, ekonomiczna = dłuższy czas oczekiwania",
                    "Sprawdzić koszty wysyłki i ewentualnego cła przy zamówieniach do Polski",
                    "Opłacić zamówienie przez oficjalny system płatności platformy",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-background-100 rounded-lg border border-background-200/70">
                      <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0 mt-0.5"><span className="text-xs font-semibold text-primary-500">{i + 1}</span></span>
                      <span className="text-sm text-foreground-700">{item}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Krok 4: Śledzenie przesyłki i odbiór</h2>
                <p className="mb-3">Po wysłaniu zamówienia otrzymasz numer śledzenia (tracking number). Możesz monitorować status przesyłki na bieżąco, korzystając z uniwersalnych trackerów takich jak 17track czy ParcelsApp.</p>
                <p className="mb-3">Standardowy czas dostawy do Polski wynosi od 10 do 25 dni roboczych. W okresach świątecznych lub przy dużym obciążeniu logistycznym czas ten może się wydłużyć.</p>
                <p>Po otrzymaniu przesyłki warto udokumentować rozpakowywanie (np. nagrać krótki film) — to dodatkowe zabezpieczenie na wypadek, gdyby produkt okazał się uszkodzony lub niezgodny z opisem.</p>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Najczęstsze błędy początkujących</h2>
                <div className="space-y-2">
                  {[
                    "Nie sprawdzanie rozmiarówki — chińskie rozmiary często różnią się od europejskich, zawsze sprawdzaj tabelę",
                    "Pomijanie zdjęć QC — to najważniejszy krok przed zatwierdzeniem wysyłki",
                    "Wybór najtańszej wysyłki bez trackingu — ryzykujesz utratę przesyłki bez możliwości śledzenia",
                    "Zamawianie bez sprawdzenia opinii sprzedawcy — kilka minut researchu może oszczędzić wiele problemów",
                    "Nieuwzględnienie kosztów celnych — VAT i cło mogą znacząco zwiększyć końcową cenę zamówienia",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-accent-100/50 rounded-lg border border-accent-200/60">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-error-warning-line text-accent-500 text-sm"></i></span>
                      <span className="text-sm text-foreground-700">{item}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section className="bg-gradient-to-r from-accent-100/60 to-primary-100/60 rounded-2xl p-6 md:p-8 text-center border border-accent-200/40">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3">Rozpocznij swoją przygodę z Kakobuy</h2>
                <p className="text-sm text-foreground-700 mb-5 max-w-md mx-auto">Skorzystaj z gotowego arkusza Kakobuy Spreadsheet i zacznij znajdować najlepsze produkty już teraz.</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a href={MAIN_SITE_URL} target="_blank" rel="noopener nofollow" className="w-full sm:w-auto bg-accent-500 text-background-50 font-semibold px-8 py-3 rounded-full hover:bg-accent-600 transition-all text-sm whitespace-nowrap cursor-pointer">Otwórz Kakobuy Spreadsheet</a>
                  <Link to="/" className="w-full sm:w-auto bg-background-50 text-foreground-900 font-semibold px-8 py-3 rounded-full border border-background-300/60 hover:bg-background-100 transition-all text-sm whitespace-nowrap cursor-pointer">Powrót na stronę główną</Link>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: s }} />
      ))}
    </div>
  );
}